import { useQuery } from "@tanstack/react-query";
import { TanstackQueryKey } from "apps/commons/tanstackQueryKey";
import {
    type UnifiedPropertyCardItem,
    getPropertyCardDisplayId,
} from "apps/oneOnboarding/complete/useCompletePagePropertyViewModel";
import { oneOnboardingApi } from "libs/api";
import { PropertyCardItemStatusEnum } from "libs/codegen/property-onboarding";
import { useParams } from "router";
import type { BulkOnboardSession } from "../BulkOnboardSession";

export interface BatchCompletePageViewModel {
    publishedListings: UnifiedPropertyCardItem[];
    failedListings: UnifiedPropertyCardItem[];
    incompleteListings: UnifiedPropertyCardItem[];
    shouldUseOnboardingId: boolean;
    shouldShowViewAllListingButton: boolean;
    viewAllListingButtonUrl: string | null;
    manageListingUrl: string | null;
    isLoading: boolean;
}

export const useBatchCompletePageViewModel = (): BatchCompletePageViewModel => {
    const { lang } = useParams("/:lang/list-my-property/onboard/batch/complete");

    const { data, isLoading } = useQuery({
        queryKey: [TanstackQueryKey.GetBatchCompleteData, lang],
        queryFn: async () => {
            const onboardSessionStorage = sessionStorage.getItem("bulkOnboardingSession");

            if (onboardSessionStorage === null) {
                throw new Error("Session data not found");
            }

            const onboardSession: BulkOnboardSession = JSON.parse(onboardSessionStorage);

            // Fetch data for all properties in a single bulk request
            const response = await oneOnboardingApi
                .getPostPublishBulk(lang, {
                    sessionId: onboardSession.sessionId,
                    bulkPropertyKeys: onboardSession.properties.map((property) => ({
                        propertyId: Number(property.propertyId),
                        onboardingId: property.onboardingId,
                    })),
                })
                .then((res) => res.data);

            return response;
        },
        staleTime: 0,
    });

    if (isLoading || !data) {
        return {
            publishedListings: [],
            failedListings: [],
            incompleteListings: [],
            shouldUseOnboardingId: false,
            shouldShowViewAllListingButton: false,
            viewAllListingButtonUrl: null,
            manageListingUrl: null,
            isLoading: true,
        };
    }

    // Extract properties from the bulk response
    const shouldUseOnboardingId = data.shouldUseOnboardingId;
    const shouldShowViewAllListingButton = data.shouldShowViewAllListingButton;
    const viewAllListingButtonUrl = data.viewAllListingButtonUrl;
    const manageListingUrl = data.manageListingUrl;

    // Transform bulk listings and separate published from failed
    const allListingsWithStatus: Array<UnifiedPropertyCardItem & { itemStatus?: PropertyCardItemStatusEnum | null }> =
        data.bulkListings?.map((card) => ({
            id: getPropertyCardDisplayId(card),
            name: card.name,
            location: card.location,
            imageUrl: card.imageUrl,
            isPublishing: card.isPublishing,
            lastModificationDate: card.lastModificationDate ?? undefined,
            onboardingUrl: card.onboardingUrl,
            itemStatus: card.itemStatus,
        })) ?? [];

    // Separate published from failed listings
    // Published listings: only itemStatus = Publishing (5)
    // Failed listings: everything else
    const publishedListings = allListingsWithStatus.filter(
        (item) => item.itemStatus === PropertyCardItemStatusEnum.NUMBER_5,
    );
    const failedListings = allListingsWithStatus.filter(
        (item) => item.itemStatus !== PropertyCardItemStatusEnum.NUMBER_5,
    );

    // Transform incomplete listings
    const incompleteListings: UnifiedPropertyCardItem[] =
        data.incompleteListings?.map((card) => ({
            id: getPropertyCardDisplayId(card),
            name: card.name,
            location: card.location,
            imageUrl: card.imageUrl,
            isPublishing: card.isPublishing,
            lastModificationDate: card.lastModificationDate ?? undefined,
            onboardingUrl: card.onboardingUrl,
        })) ?? [];

    return {
        publishedListings,
        failedListings,
        incompleteListings,
        shouldUseOnboardingId,
        shouldShowViewAllListingButton,
        viewAllListingButtonUrl,
        manageListingUrl,
        isLoading: false,
    };
};
